import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeMount, reactive, ref } from 'vue';
import RequiredFieldsHint from '../forms/RequiredFieldsHint.vue';
import ShiftModelSelect from '@/components/inputs/ShiftModelSelect.vue';
import CustomersSelect from '@/components/inputs/CustomersSelect.vue';
import LocationsSelect from '@/components/inputs/LocationsSelect.vue';
import { Customer } from '@/models/tenant';
import { Device } from '@/models/device';
import { Mode } from '@/models/enums/Mode';
import { UserType } from '@/models/userType';
import { hasCurrentUserPermission } from '@/utils/permissionUtils';
import { devicesService } from '@/services/devices.service';
import { isNil } from '@/utils/misc';
import { DeviceBaseModel, DeviceModelType } from '@/models/deviceBaseModel';
import { usersService } from '@/services/users.service';
import { today } from '@/utils/dates';
import useVuelidate from '@vuelidate/core';
import {
  withLocalizedValidators,
  maxLength,
  required,
  requiredIf,
  maxDate,
} from '@/utils/validation';
import { helpers } from '@vuelidate/validators';
import { useI18n } from '@/composables/i18n';
import { BetaFeature } from '@/models/enums/BetaFeature';
import { TranslateResult } from 'vue-i18n';
import { User } from '@/models/user';
import { DeviceUpsertData } from '@/components/devices/models';


export default /*#__PURE__*/_defineComponent({
  __name: 'DeviceFormModal',
  props: {
    mode: null,
    customer: null,
    device: null
  },
  emits: ["save", "close"],
  setup(__props: any, { emit }: { emit: ((e: 'save' | 'close') => void), expose: any, slots: any, attrs: any }) {

const props = __props as {
  mode: Mode;
  customer?: Customer;
  device?: Device;
};





const { t } = useI18n();

const isSubmitting = ref(false);
const state = reactive({
  // Nesting formData here is the only way to make the assignment of
  // structuredClone to work. Otherwise, it's preferable not to nest stuff in a
  // reactive property.
  formData: {
    name: '',
    customerId: 0,
    locationId: undefined,
    deviceBaseModelId: undefined,
    billable: true,
    hasWCC: false,
    hasTechnology: false,
    hasCare: false,
    ip: '',
    sscIp: '',
    equipmentId: '',
    cameraIp: '',
    startDate: undefined,
    endDate: undefined,
    technologyStartDate: undefined,
    technologyEndDate: undefined,
    careStartDate: undefined,
    careEndDate: undefined,
    shiftModelId: undefined,
  } as Partial<DeviceUpsertData>,
});

const IP_WITH_OPTIONAL_PORT_REGEX =
  /^((25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(25[0-5]|2[0-4]\d|[01]?\d\d?)(?::(\d|[1-9]\d{1,3}|[1-5]\d{4}|6[0-4]\d{3}|65[0-4]\d{2}|655[0-2]\d|6553[0-5]))?$/;
const ipWithOptionalPort = helpers.regex(IP_WITH_OPTIONAL_PORT_REGEX);
const validations = computed(() => ({
  formData: {
    name: withLocalizedValidators('device.device_name', {
      required,
      nameRegex: helpers.regex(/^[\w\p{L} .,-]{1,80}$/u),
    }),
    customerId: withLocalizedValidators('inputs.customers_select.label', {
      required,
    }),
    locationId: withLocalizedValidators('inputs.locations_select.label', {
      required,
    }),
    deviceBaseModelId: withLocalizedValidators('device.baseModelName', {
      required,
    }),
    ip: withLocalizedValidators('device.ip', { required, ipWithOptionalPort }),
    sscIp: withLocalizedValidators('device.ssc_ip', {
      required: requiredIf(() => isBvcDevice.value),
      ipWithOptionalPort,
    }),
    equipmentId: withLocalizedValidators('device.equipmentId', {
      required,
      maxLength: maxLength(15),
    }),
    cameraIp: withLocalizedValidators('device.cameraIp', {
      ipWithOptionalPort: (value) =>
        !helpers.req(value) || ipWithOptionalPort(value),
    }),
    careEndDate: withLocalizedValidators('device.careEndDate', {
      required: requiredIf(() => isCareEndDateRequired.value),
      // Care end date can't be greater than Work Center Console's, if specified
      maxDate: maxDate(state.formData.endDate),
    }),
  },
}));
const v$ = useVuelidate(validations, state);

onBeforeMount(() => {
  if (!isNil(props.device)) {
    state.formData = structuredClone(props.device);
    state.formData.customerId = props.device.tenantId;
    state.formData.shiftModelId ??= -1;
  } else if (isCreateMode.value) {
    // !: Creation is only allowed from Devices tab from a tenant, so
    // this.tenant must be defined.
    state.formData.customerId = props.customer!.id;
  }
});

async function onSubmit() {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) {
    return;
  }

  if (!isBvcDevice.value) {
    state.formData.sscIp = undefined;
  }

  const deviceUpsertData = formDataToUpsertData(state.formData);
  deviceUpsertData.id = props.device?.id;

  isSubmitting.value = true;
  devicesService
    .upsert(deviceUpsertData)
    .then(() => {
      emit('save');
      emit('close');
    })
    .finally(() => (isSubmitting.value = false));
}

async function handleDeviceBaseModelIdSelected(modelId: string) {
  if (!devicesService.store.isBVC(modelId)) {
    state.formData.hasTechnology = false;
    state.formData.hasCare = false;
  }
}

function onWorkCenterSwitchChanged(isEnabled: boolean) {
  state.formData.endDate = null;

  if (isEnabled) {
    state.formData.startDate = today();
  } else {
    state.formData.cameraIp = null;
    state.formData.startDate = null;
  }
}

function onTechnologySwitchChanged(isEnabled: boolean) {
  state.formData.technologyEndDate = null;
  state.formData.technologyStartDate = isEnabled ? today() : null;
}

function onCareSwitchChanged(isEnabled: boolean) {
  state.formData.careEndDate = null;

  if (isEnabled) {
    state.formData.careStartDate = today();
    if (!state.formData.hasWCC) {
      enableWorkCenterConsole();
    }
  } else {
    state.formData.careStartDate = null;
  }
}

function enableWorkCenterConsole() {
  state.formData.hasWCC = true;
  onWorkCenterSwitchChanged(true);
}

function formDataToUpsertData(formData: Partial<DeviceUpsertData>): DeviceUpsertData {
  return {
    ...formData,
    shiftModelId: formData.shiftModelId === -1 ? null : formData.shiftModelId,
  } as DeviceUpsertData; // validation ensures this cast is safe
}

function setToMidnight(date: Date) {
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0),
  );
}

function onDateFieldChanged(date: Date | null, field: keyof Device) {
  if (!date) {
    return;
  }

  (state.formData as any)[field] = setToMidnight(date);
}

function getDeviceModelsByType(value: DeviceModelType): DeviceBaseModel[] {
  switch (value) {
    case DeviceModelType.Laser:
      return devicesService.store.getAllLaserModels();
    case DeviceModelType.Bending:
      return devicesService.store.getAllBendModels();
    case DeviceModelType.Tubes:
      return devicesService.store.getAllTubeModels();
    default:
      return [];
  }
}

function getErrorMessage(key: keyof typeof state.formData): string | undefined {
  return v$.value.formData[key].$errors?.[0]?.$message;
}

const isEditMode = computed<boolean>(() => props.mode === Mode.Edit);

const isCreateMode = computed<boolean>(() => props.mode === Mode.Create);

const canEditEquipmentNameAndShift = computed<boolean>(() =>
  hasCurrentUserPermission(UserType.CustomerAdmin),
);

const actionTitle = computed<string>(() => {
  if (isCreateMode.value) {
    return t('action.create').toString();
  } else if (isEditMode.value) {
    if (!canEditEquipmentNameAndShift.value) {
      return t('action.view').toString();
    } else {
      return t('action.save').toString();
    }
  } else {
    return t('action.close').toString();
  }
});

const modalTitle = computed<TranslateResult>(() => {
  return isEditMode.value ? t(`action.edit`) : t('action.create');
});

const isBvcDevice = computed<boolean>(() =>
  devicesService.store.isBVC(state.formData.deviceBaseModelId!),
);

const canEditEquipment = computed<boolean>(() =>
  hasCurrentUserPermission(UserType.SubsidiaryAdmin),
);

const canToggleWorkCenterConsole = computed<boolean>(
  () => canEditEquipment.value && !state.formData.hasCare,
);

const canEnableCareConsole = computed<boolean>(() =>
  hasCurrentUserPermission(UserType.SubsidiaryAdmin),
);

const canEnableTechnologyConsole = computed<boolean>(() => canEnableCareConsole.value);

const currentUser = computed<User>(() => usersService.store.current());

const deviceModelTypes = computed<DeviceModelType[]>(() => Object.values(DeviceModelType));

const canClearEndDate = computed<boolean>(
  () => !isNil(state.formData.endDate) && canEditEquipment.value,
);

const canClearTechnologyEndDate = computed<boolean>(
  () => !isNil(state.formData.technologyEndDate) && canEditEquipment.value,
);

const canClearCareEndDate = computed<boolean>(
  () => !isNil(state.formData.careEndDate) && canEditEquipment.value,
);

const canToggleBillable = computed<boolean>(
  () =>
    currentUser.value.isGlobalAdmin &&
    currentUser.value.hasBetaFeature(BetaFeature.BackOfficeDeviceBillable),
);

const isCareEndDateRequired = computed<boolean>(
  () => (state.formData.hasCare && !isNil(state.formData.endDate)) ?? false,
);

return { __sfc: true,props, emit, t, isSubmitting, state, IP_WITH_OPTIONAL_PORT_REGEX, ipWithOptionalPort, validations, v$, onSubmit, handleDeviceBaseModelIdSelected, onWorkCenterSwitchChanged, onTechnologySwitchChanged, onCareSwitchChanged, enableWorkCenterConsole, formDataToUpsertData, setToMidnight, onDateFieldChanged, getDeviceModelsByType, getErrorMessage, isEditMode, isCreateMode, canEditEquipmentNameAndShift, actionTitle, modalTitle, isBvcDevice, canEditEquipment, canToggleWorkCenterConsole, canEnableCareConsole, canEnableTechnologyConsole, currentUser, deviceModelTypes, canClearEndDate, canClearTechnologyEndDate, canClearCareEndDate, canToggleBillable, isCareEndDateRequired, RequiredFieldsHint, ShiftModelSelect, CustomersSelect, LocationsSelect }
}

})